/* SKARVO cart suggestions
   - mode="empty":  scarves the visitor looked at, then SKARVO picks
   - mode="recent": "Add another scarf" = scarves the visitor looked at but did not put in the cart
   Viewed products are saved in localStorage (key skvViewed) on every product page.
   The theme re-renders the cart drawer after each change, so the element watches itself
   and rebuilds its cards whenever the theme wipes them. */
(() => {
  if (customElements.get('skarvo-cart-suggest')) return;

  const KEY = 'skvViewed';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cache = new Map();

  const getViewed = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
  };

  const fetchProduct = (h) => {
    if (!cache.has(h)) {
      cache.set(h, fetch(root + 'products/' + encodeURIComponent(h) + '.js')
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null));
    }
    return cache.get(h);
  };

  const sized = (src) => {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    return src + (src.includes('?') ? '&' : '?') + 'width=300';
  };

  const money = (cents, fmt) => {
    const v = cents / 100;
    const m = (fmt || '{{amount}}').match(/\{\{\s*(\w+)\s*\}\}/);
    const type = m ? m[1] : 'amount';
    const num = (n, d, ts, ds) => {
      const [i, f] = n.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (f ? ds + f : '');
    };
    let s;
    switch (type) {
      case 'amount_no_decimals': s = num(v, 0, ',', '.'); break;
      case 'amount_with_comma_separator': s = num(v, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': s = num(v, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': s = num(v, 2, "'", '.'); break;
      case 'amount_with_space_separator': s = num(v, 2, ' ', ','); break;
      default: s = num(v, 2, ',', '.');
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = (fmt || '{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, s);
    return tmp.textContent.trim();
  };

  const el = (tag, attrs, kids) => {
    const n = document.createElement(tag);
    for (const k in attrs || {}) {
      if (k === 'text') n.textContent = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach((c) => c && n.appendChild(c));
    return n;
  };

  const fromJs = (p, fmt) => {
    const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
    return {
      handle: p.handle,
      title: p.title,
      url: p.url || root + 'products/' + p.handle,
      image: sized(p.featured_image),
      price: money(p.price, fmt),
      variant: v ? v.id : null,
      single: (p.variants || []).length <= 1,
    };
  };

  class SkarvoCartSuggest extends HTMLElement {
    connectedCallback() {
      this.render();

      if (!this._onCart) {
        this._onCart = () => this.schedule(1200);
        document.addEventListener('shopify:cart:lines-update', this._onCart);
      }

      if (!this._mo) {
        // The theme morphs the drawer after cart changes and wipes our cards: rebuild them.
        this._mo = new MutationObserver(() => {
          if (!this._busy) this.schedule(250);
        });
        this._mo.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
      }
    }

    disconnectedCallback() {
      if (this._onCart) document.removeEventListener('shopify:cart:lines-update', this._onCart);
      this._onCart = null;
      if (this._mo) this._mo.disconnect();
      this._mo = null;
      clearTimeout(this._timer);
    }

    schedule(ms) {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.isConnected && this.render(), ms);
    }

    write(fn) {
      this._busy = true;
      try { fn(); } finally { setTimeout(() => { this._busy = false; }, 0); }
    }

    async render() {
      const token = (this._token = (this._token || 0) + 1);
      const mode = this.dataset.mode || 'recent';
      const fmt = this.dataset.moneyFormat || '{{amount}}';

      let picks = [];
      try { picks = JSON.parse(this.querySelector('[data-picks]').textContent || '[]'); } catch (e) {}

      const inCart = new Set();
      try {
        const cart = await fetch(root + 'cart.js').then((r) => r.json());
        (cart.items || []).forEach((i) => inCart.add(i.handle));
      } catch (e) {}

      const handles = getViewed().filter((h) => !inCart.has(h)).slice(0, 8);
      const viewed = (await Promise.all(handles.map(fetchProduct)))
        .filter((p) => p && p.available)
        .map((p) => fromJs(p, fmt));

      if (token !== this._token || !this.isConnected) return; // a newer render started

      let items;
      let heading;
      if (mode === 'empty') {
        const seen = new Set(viewed.map((p) => p.handle));
        items = viewed.concat(picks.filter((p) => !seen.has(p.handle) && !inCart.has(p.handle))).slice(0, 8);
        heading = viewed.length ? 'Still thinking about these? 👀' : 'Pick your scarf 🧣';
      } else {
        items = viewed.length ? viewed : picks.filter((p) => !inCart.has(p.handle)).slice(0, 6);
        heading = viewed.length ? 'Add another scarf 🧣 – you looked at these' : 'Add another scarf 🧣';
      }

      this.write(() => {
        let title = this.querySelector('[data-title]');
        let list = this.querySelector('[data-list]');
        if (!title) { title = el('p', { class: 'skv-cs__title', 'data-title': '' }); this.prepend(title); }
        if (!list) { list = el('ul', { class: 'skv-cs__list', 'data-list': '', role: 'list' }); title.after(list); }

        if (!items.length) {
          this.hidden = true;
          list.replaceChildren();
          return;
        }
        this.hidden = false;
        title.textContent = heading;
        list.replaceChildren(...items.map((p) => this.card(p)));
      });
    }

    card(p) {
      const img = p.image ? el('img', { src: p.image, alt: p.title, loading: 'lazy', width: '300', height: '300' }) : null;
      let action;
      if (p.single && p.variant) {
        action = el('button', { type: 'button', class: 'button skv-cs__btn', text: '+ Add' });
        action.addEventListener('click', () => this.add(p, action));
      } else {
        action = el('a', { href: p.url, class: 'button button-secondary skv-cs__btn', text: 'Choose' });
      }
      return el('li', { class: 'skv-cs__card' }, [
        el('a', { href: p.url, class: 'skv-cs__img' }, [img]),
        el('a', { href: p.url, class: 'skv-cs__name', text: p.title }),
        el('span', { class: 'skv-cs__price', text: p.price }),
        action,
      ]);
    }

    async add(p, btn) {
      this.write(() => {
        btn.disabled = true;
        btn.textContent = 'Adding…';
      });
      try {
        const actions = window.Shopify && window.Shopify.actions;
        if (actions && actions.updateCart) {
          const res = await actions.updateCart(
            { lines: [{ merchandiseId: String(p.variant), quantity: 1 }] },
            { event: { context: 'standard-action' } }
          );
          if (res && res.userErrors && res.userErrors.length) throw new Error(res.userErrors[0].message);
          // safety net: if the drawer did not refresh, reload so the scarf shows up
          setTimeout(() => {
            if (document.querySelector('.cart-drawer--empty')) location.reload();
          }, 2500);
        } else {
          const r = await fetch(root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: p.variant, quantity: 1 }] }),
          });
          if (!r.ok) throw new Error('add failed');
          location.reload();
        }
      } catch (e) {
        location.href = p.url; // let the customer add it from the product page
      }
    }
  }

  customElements.define('skarvo-cart-suggest', SkarvoCartSuggest);
})();
